/**
 * Recovering a token an app's own backend refused with a plain 401.
 *
 * The stored token is a cache of the session cookie, and it can go stale while
 * the session is still live — a laptop that slept through a renewal is the
 * usual case. An API client that clears its token on that 401 signs out a
 * person who is signed in. The right response is to renew from the cookie and
 * retry, and only clear when the server says the session itself is over.
 *
 * The renewal runs inside `AppAuthProvider`, not the client: the provider
 * already single-flights renewals, tracks the idle deadline the renewal
 * reports, and knows how to end a session with its reason ("signed out for
 * inactivity"). The provider registers the recoverer; clients await it.
 *
 * Opt-in: a client that never calls `recoverRefusedToken` behaves as before.
 */
import { decodeJwtPayload } from './sessionToken';
let recoverer = null;
let ender = null;
/** Called by `AppAuthProvider` on mount. Returns the unregister function. */
export function registerTokenRecovery(fn, end) {
    recoverer = fn;
    ender = end ?? null;
    return () => {
        if (recoverer === fn) {
            recoverer = null;
            ender = null;
        }
    };
}
/**
 * Hand the provider a token the app's backend refused even after recovery —
 * a replay with the freshly renewed token, say — so it signs out with a reason.
 *
 * Returns `false` when no provider has registered; the caller then clears its
 * own token as before.
 */
export function endUnusableSession() {
    if (!ender)
        return false;
    ender();
    return true;
}
/**
 * Ask the provider to renew a token the caller's backend refused.
 *
 * Pass the token the refused request actually sent. When the stored token has
 * already moved on from it, another refusal's recovery got there first and the
 * answer is `renewed` straight away.
 *
 * A renewal derives from the cookie, which may name a different user or org
 * than the refused token. `renewed` does not promise the same identity: before
 * replaying a write, compare the two (`isSameSessionIdentity`).
 *
 * Returns `null` when no provider has registered (a client used outside the
 * provider), which is the caller's signal to fall back to its own 401 handling.
 */
export async function recoverRefusedToken(refusedToken) {
    if (!recoverer)
        return null;
    try {
        return await recoverer(refusedToken);
    }
    catch {
        return 'unreachable';
    }
}
/**
 * Do two tokens name the same user in the same organization?
 *
 * The check before replaying a write under a renewed token: a renewal that
 * adopted the cookie's identity would otherwise land the caller's payload on
 * another account. Unreadable tokens never match.
 */
export function isSameSessionIdentity(a, b) {
    const left = a ? decodeJwtPayload(a) : null;
    const right = b ? decodeJwtPayload(b) : null;
    if (!left?.sub || !right?.sub)
        return false;
    return left.sub === right.sub && (left.tenantId ?? null) === (right.tenantId ?? null);
}
